/**
 * Auth Service
 *
 * Encapsulates all MSAL operations with clean async/await API.
 * This is a singleton service that manages the MSAL instance.
 */
import { PublicClientApplication, type AccountInfo, type AuthenticationResult } from '@azure/msal-browser';
import { createMsalConfig, createLoginRequest, createSilentRequest, getStorageKeyPrefix, getScopes } from '../config/msal';
import { AuthError } from '../types/errors';
import type { TokenClaims, SignInResult } from '../types/auth';
import { api, API_V1 } from '@v2/services/api/client';

/**
 * Decode JWT token to extract claims
 */
function decodeToken(token: string): TokenClaims {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload) as TokenClaims;
  } catch (error) {
    throw new AuthError('Failed to decode token', 'UNKNOWN', error);
  }
}

/**
 * Check if token claims are expired
 */
function isTokenExpired(claims: TokenClaims): boolean {
  if (!claims.exp) return true;
  // Add 60 second buffer for clock skew
  return Date.now() >= (claims.exp * 1000) - 60000;
}

/**
 * AuthService class - singleton MSAL wrapper
 */
class AuthService {
  private msalInstance: PublicClientApplication | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Lazily initialize MSAL instance
   */
  private async ensureMsal(): Promise<PublicClientApplication> {
    if (this.msalInstance) {
      return this.msalInstance;
    }

    // Prevent multiple initializations
    if (this.initPromise) {
      await this.initPromise;
      return this.msalInstance!;
    }

    this.initPromise = this.createMsalInstance();
    await this.initPromise;
    return this.msalInstance!;
  }

  /**
   * Create and initialize MSAL instance
   */
  private async createMsalInstance(): Promise<void> {
    try {
      const config = createMsalConfig();
      this.msalInstance = new PublicClientApplication(config);
      // Note: MSAL v2.x doesn't require explicit initialize() call
      // v3.x would need: await this.msalInstance.initialize();
      console.debug('[AuthService] MSAL initialized');
    } catch (error) {
      this.initPromise = null;
      throw AuthError.from(error, 'INITIALIZATION_FAILED');
    }
  }

  /**
   * Initialize and check for existing session
   * Returns token and claims if valid session exists, null otherwise
   */
  async initialize(): Promise<SignInResult | null> {
    const msal = await this.ensureMsal();

    // Check for active account
    const activeAccount = msal.getActiveAccount();
    if (activeAccount) {
      return this.tryRestoreSession(msal, activeAccount);
    }

    // Check all accounts, get most recently issued
    const accounts = msal.getAllAccounts();
    if (accounts.length > 0) {
      // Sort by exp descending
      const sortedAccounts = [...accounts].sort((a, b) => {
        const expA = (a.idTokenClaims as TokenClaims)?.exp ?? 0;
        const expB = (b.idTokenClaims as TokenClaims)?.exp ?? 0;
        return expB - expA;
      });

      const bestAccount = sortedAccounts[0];
      const claims = bestAccount.idTokenClaims as TokenClaims | undefined;

      if (claims && !isTokenExpired(claims)) {
        return this.tryRestoreSession(msal, bestAccount);
      }
    }

    console.debug('[AuthService] No valid session found');
    return null;
  }

  /**
   * Try to restore session from existing account
   */
  private async tryRestoreSession(
    msal: PublicClientApplication,
    account: AccountInfo
  ): Promise<SignInResult | null> {
    const claims = account.idTokenClaims as TokenClaims | undefined;

    // Check if session is expired
    if (!claims || isTokenExpired(claims)) {
      console.debug('[AuthService] Session expired, clearing');
      msal.setActiveAccount(null);
      return null;
    }

    // Try to get fresh token silently
    try {
      const result = await msal.acquireTokenSilent({
        ...createSilentRequest(),
        account,
      });

      if (result?.idToken) {
        msal.setActiveAccount(account);
        const tokenClaims = decodeToken(result.idToken);
        console.debug('[AuthService] Session restored');
        return { token: result.idToken, claims: tokenClaims };
      }
    } catch (error) {
      console.debug('[AuthService] Silent token acquisition failed:', error);
    }

    // Fallback: try to extract token from storage
    const token = this.extractTokenFromStorage(msal);
    if (token) {
      try {
        const tokenClaims = decodeToken(token);
        if (!isTokenExpired(tokenClaims)) {
          msal.setActiveAccount(account);
          console.debug('[AuthService] Session restored from storage');
          return { token, claims: tokenClaims };
        }
      } catch {
        // Token decode failed, continue
      }
    }

    console.debug('[AuthService] Could not restore session');
    return null;
  }

  /**
   * Extract token from MSAL storage
   */
  private extractTokenFromStorage(msal: PublicClientApplication): string | null {
    try {
      // Access MSAL's internal storage
      const storage = (msal as unknown as { browserStorage: { browserStorage: { windowStorage: Storage } } })
        .browserStorage?.browserStorage?.windowStorage;

      if (!storage) return null;

      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.includes('idtoken')) {
          const value = storage.getItem(key);
          if (value) {
            const parsed = JSON.parse(value);
            if (parsed?.secret) {
              return parsed.secret;
            }
          }
        }
      }
    } catch {
      // Storage access failed
    }
    return null;
  }

  /**
   * Sign in via popup
   * @param idpHint - Optional identity provider hint (e.g. 'google.com') to skip B2C provider selection
   */
  async signIn(idpHint?: string): Promise<SignInResult> {
    const msal = await this.ensureMsal();

    try {
      const result: AuthenticationResult = await msal.loginPopup(createLoginRequest(idpHint));

      if (!result?.account || !result?.idToken) {
        throw new AuthError('Login did not return account or token', 'UNKNOWN');
      }

      msal.setActiveAccount(result.account);
      const claims = decodeToken(result.idToken);

      console.debug('[AuthService] Sign in successful');
      return { token: result.idToken, claims };
    } catch (error) {
      throw AuthError.from(error, 'UNKNOWN');
    }
  }

  /**
   * Sign out and clear all session data
   */
  async signOut(): Promise<void> {
    const msal = await this.ensureMsal();

    // Get accounts for logout
    const accounts = msal.getAllAccounts();

    if (accounts.length > 0) {
      try {
        // Initiate B2C logout redirect
        await msal.logoutRedirect({
          account: accounts[0],
        });
      } catch (error) {
        console.warn('[AuthService] Logout redirect failed:', error);
      }
    }

    // Clear all B2C and MSAL storage
    this.clearStorage();

    console.debug('[AuthService] Sign out complete');
  }

  /**
   * Clear all auth-related storage
   */
  private clearStorage(): void {
    const prefix = getStorageKeyPrefix();

    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes(prefix) || key.startsWith('msal'))) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  /**
   * Refresh token silently
   */
  async refreshToken(): Promise<SignInResult> {
    const msal = await this.ensureMsal();
    const account = msal.getActiveAccount();

    if (!account) {
      throw new AuthError('No active account for token refresh', 'SESSION_NOT_FOUND');
    }

    try {
      const result = await msal.acquireTokenSilent({
        ...createSilentRequest(),
        account,
      });

      if (!result?.idToken) {
        throw new AuthError('Token refresh did not return token', 'TOKEN_REFRESH_FAILED');
      }

      const claims = decodeToken(result.idToken);
      console.debug('[AuthService] Token refreshed');
      return { token: result.idToken, claims };
    } catch (error) {
      throw AuthError.from(error, 'TOKEN_REFRESH_FAILED');
    }
  }

  /**
   * Get impersonation token for target user (admin only)
   */
  async getImpersonationToken(userId: string): Promise<string> {
    try {
      const response = await api.post<{ token: string }>(
        `${API_V1}/user/${userId}/impersonate`
      );

      if (!response?.token) {
        throw new AuthError('Impersonation did not return token', 'IMPERSONATION_FAILED');
      }

      console.debug('[AuthService] Impersonation token acquired');
      return response.token;
    } catch (error) {
      if (error instanceof AuthError) throw error;
      throw new AuthError('Failed to get impersonation token', 'IMPERSONATION_FAILED', error);
    }
  }

  /**
   * Get the MSAL instance (for advanced use)
   */
  async getMsalInstance(): Promise<PublicClientApplication> {
    return this.ensureMsal();
  }
}

/**
 * Singleton auth service instance
 */
export const authService = new AuthService();
